"use client";

import { Suspense, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { destinationSure } from "@/lib/callback-url";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/shared/AddressAutocomplete";
import { CompanyAutocomplete } from "@/components/shared/CompanyAutocomplete";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

function RegisterForm() {
  const t = useTranslations("auth");
  const router = useRouter();
  const params = useSearchParams();
  const retour = destinationSure(params.get("callbackUrl"));
  const [name, setName] = useState("");
  // Adresse pré-remplie quand on arrive d'une invitation : l'invitation est
  // rattachée à CETTE adresse, s'inscrire avec une autre ne mènerait nulle part.
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // ── Facturation ──
  const [accountType, setAccountType] = useState<"PERSONAL" | "COMPANY">("PERSONAL");
  const [companyName, setCompanyName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [siret, setSiret] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingPostalCode, setBillingPostalCode] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingCountry, setBillingCountry] = useState("France");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("passwordMismatch"));
      return;
    }

    if (password.length < 8) {
      setError(t("passwordTooShort"));
      return;
    }

    if (accountType === "COMPANY" && !companyName.trim()) {
      setError("La raison sociale est requise pour un compte entreprise.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email, password,
          accountType,
          companyName, vatNumber, siret,
          billingAddress, billingPostalCode, billingCity, billingCountry,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || t("genericError"));
        setLoading(false);
        return;
      }

      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError(t("accountCreatedLoginFailed"));
        router.push("/login");
        return;
      }

      router.push(retour);
    } catch {
      setError(t("genericError"));
      setLoading(false);
    }
  }

  const champ = "border-border bg-[var(--bg-tertiary)]";

  return (
    <Card className="mx-auto max-w-3xl border-border bg-[var(--bg-secondary)]">
      <CardHeader>
        <CardTitle className="text-foreground">{t("createAccountTitle")}</CardTitle>
        <CardDescription>{t("createAccountSubtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* ── Deux colonnes : le compte à gauche, la facturation à droite.
                Sur mobile elles s'empilent. ── */}
          <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
            {/* ─────────── Votre compte ─────────── */}
            <section className="space-y-4">
              <h3 className="border-b border-border pb-2 text-sm font-semibold text-foreground">
                Votre compte
              </h3>

              <div className="space-y-1.5">
                <Label htmlFor="name">{t("fullName")}</Label>
                <Input id="name" type="text" placeholder={t("fullNamePlaceholder")}
                  value={name} onChange={(e) => setName(e.target.value)} required className={champ} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("email")}</Label>
                <Input id="email" type="email" placeholder={t("emailPlaceholder")}
                  value={email} onChange={(e) => setEmail(e.target.value)} required className={champ} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t("password")}</Label>
                  <Input id="password" type="password" placeholder={t("passwordPlaceholder")}
                    value={password} onChange={(e) => setPassword(e.target.value)} required className={champ} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">{t("confirmPassword")}</Label>
                  <Input id="confirmPassword" type="password"
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={champ} />
                </div>
              </div>

            </section>

            {/* ─────────── Facturation ─────────── */}
            <section className="space-y-4">
              <h3 className="border-b border-border pb-2 text-sm font-semibold text-foreground">
                Facturation
              </h3>

              {/* Particulier / Entreprise — segmenté */}
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-[var(--bg-tertiary)] p-1">
                {(["PERSONAL", "COMPANY"] as const).map((t2) => (
                  <button key={t2} type="button" onClick={() => setAccountType(t2)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      accountType === t2
                        ? "bg-[var(--accent-primary)] text-white"
                        : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {t2 === "PERSONAL" ? "Particulier" : "Entreprise"}
                  </button>
                ))}
              </div>

              {accountType === "COMPANY" && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="companyName">Raison sociale</Label>
                    <CompanyAutocomplete
                      id="companyName"
                      placeholder="ACME SAS"
                      className={champ}
                      value={companyName}
                      onChange={setCompanyName}
                      onSelect={(e) => {
                        setCompanyName(e.companyName);
                        if (e.siret) setSiret(e.siret);
                        if (e.vatNumber) setVatNumber(e.vatNumber);
                        // L'adresse du siège pré-remplit la facturation, modifiable.
                        if (e.address) setBillingAddress(e.address);
                        if (e.postalCode) setBillingPostalCode(e.postalCode);
                        if (e.city) setBillingCity(e.city);
                        setBillingCountry("France");
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="vat">N° TVA</Label>
                      <Input id="vat" placeholder="FR12345678901"
                        value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} className={champ} />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="siret">SIRET</Label>
                      <Input id="siret" placeholder="123 456 789 00011"
                        value={siret} onChange={(e) => setSiret(e.target.value)} className={champ} />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="addr">Adresse</Label>
                <AddressAutocomplete
                  id="addr"
                  placeholder="12 rue des Lilas"
                  className={champ}
                  value={billingAddress}
                  onChange={setBillingAddress}
                  onSelect={({ address, postalCode, city }) => {
                    setBillingAddress(address);
                    setBillingPostalCode(postalCode);
                    setBillingCity(city);
                    // L'API ne sert que la France : on aligne le pays.
                    setBillingCountry("France");
                  }}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cp">Code postal</Label>
                  <Input id="cp" placeholder="75001"
                    value={billingPostalCode} onChange={(e) => setBillingPostalCode(e.target.value)} className={champ} />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="ville">Ville</Label>
                  <Input id="ville" placeholder="Paris"
                    value={billingCity} onChange={(e) => setBillingCity(e.target.value)} className={champ} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pays">Pays</Label>
                <Input id="pays" value={billingCountry}
                  onChange={(e) => setBillingCountry(e.target.value)} className={champ} />
              </div>
            </section>
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t("createAccountLoading") : t("createAccount")}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          {t("hasAccount")}{" "}
          <Link
            href="/login"
            className="text-[var(--accent-primary)] hover:underline"
          >
            {t("loginAction")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}

/** Voir le commentaire de la page de connexion : `useSearchParams` + Suspense. */
export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterForm />
    </Suspense>
  );
}
